import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Skeleton,
} from '@forgenetwork/ui';
import { api } from '@/lib/api';
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Activity,
  RefreshCw,
  GitBranch,
  Eye,
} from 'lucide-react';

const searchSchema = z.object({
  repo: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/governance')({
  component: GovernancePage,
  validateSearch: searchSchema,
});

interface GovernanceData {
  repository: {
    id: string;
    name: string;
    slug: string;
  };
  complianceScore: number;
  driftDetections: number;
  policyViolations: number;
  lastScan: string | null;
  frameworks: Array<{
    name: string;
    status: 'compliant' | 'non_compliant' | 'partial';
    score: number;
  }>;
  recentEvents: Array<{
    id: string;
    type: string;
    description: string;
    timestamp: string;
    severity: 'info' | 'warning' | 'critical';
  }>;
}

function GovernancePage(): React.JSX.Element {
  const search = Route.useSearch() as { repo?: string };
  const repoSlug = search.repo;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['governance', repoSlug],
    queryFn: async (): Promise<GovernanceData> => {
      if (!repoSlug) {
        throw new Error('Repository slug is required');
      }

      // Fetch from real governance API endpoint
      const response = await api.get<{
        repo: string;
        reliability: {
          overallScore: number;
        };
        compliance: {
          soc2Controls: Array<{ status: string }>;
          securityPosture: { overallScore: number };
        };
        sddIntegrity: {
          sddDeltas: Array<{ divergenceWarnings: Array<{ severity: string }> }>;
        };
        violations: {
          violations: Array<{
            id: string;
            type: string;
            description: string;
            severity: string;
            createdAt: string;
          }>;
          summary: {
            total: number;
            unresolved: number;
          };
        };
        ciHealth: {
          recentRuns: Array<{ createdAt: string }>;
        };
      }>(`/governance/${repoSlug}`);

      // Calculate compliance score from SOC2 controls
      const passingControls = response.compliance.soc2Controls.filter(c => c.status === 'pass').length;
      const totalControls = response.compliance.soc2Controls.length || 1;
      const complianceScore = Math.round((passingControls / totalControls) * 100);

      // Count drift detections from SDD deltas with high/medium warnings
      const driftDetections = response.sddIntegrity.sddDeltas.reduce((count, delta) => {
        return count + delta.divergenceWarnings.filter(w => w.severity === 'high' || w.severity === 'medium').length;
      }, 0);

      // Get last scan timestamp from most recent CI run
      const lastScan = response.ciHealth.recentRuns[0]?.createdAt ?? null;

      // Transform violations to recent events
      const recentEvents = response.violations.violations.slice(0, 5).map((v) => ({
        id: v.id,
        type: v.type,
        description: v.description,
        timestamp: v.createdAt,
        severity: v.severity as 'info' | 'warning' | 'critical',
      }));

      // Build framework compliance data
      const soc2Score = complianceScore;
      const securityScore = response.compliance.securityPosture.overallScore;

      return {
        repository: {
          id: repoSlug,
          name: repoSlug,
          slug: repoSlug,
        },
        complianceScore,
        driftDetections,
        policyViolations: response.violations.summary.unresolved,
        lastScan,
        frameworks: [
          {
            name: 'SOC2',
            status: soc2Score >= 90 ? 'compliant' : soc2Score >= 70 ? 'partial' : 'non_compliant',
            score: soc2Score
          },
          {
            name: 'Security',
            status: securityScore >= 90 ? 'compliant' : securityScore >= 70 ? 'partial' : 'non_compliant',
            score: securityScore
          },
          {
            name: 'Reliability',
            status: response.reliability.overallScore >= 90 ? 'compliant' : response.reliability.overallScore >= 70 ? 'partial' : 'non_compliant',
            score: response.reliability.overallScore
          },
        ],
        recentEvents,
      };
    },
    enabled: !!repoSlug,
  });

  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'compliant':
        return <Badge className="bg-green-500">Compliant</Badge>;
      case 'non_compliant':
        return <Badge variant="destructive">Non-Compliant</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-500">Partial</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Activity className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Show empty state when no repository is selected
  if (!repoSlug) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Governance</h1>
              <p className="text-muted-foreground mt-1">
                Monitor compliance, security posture, and policy enforcement
              </p>
            </div>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Select a Repository</CardTitle>
              <CardDescription>
                Choose a repository from the repositories page to view its governance dashboard.
                Navigate to a repository and click &quot;View Governance&quot; to see compliance metrics,
                drift detection, and policy violations.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Error Loading Governance</CardTitle>
            <CardDescription>
              {error instanceof Error ? error.message : 'Failed to load governance data'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Governance</h1>
                {repoSlug && (
                  <div className="flex items-center gap-2 text-muted-foreground mt-1">
                    <GitBranch className="h-4 w-4" />
                    <span>{repoSlug}</span>
                  </div>
                )}
              </div>
            </div>
            <p className="text-muted-foreground mt-2">
              Monitor compliance, security posture, and policy enforcement
            </p>
          </div>
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardHeader className="pb-2">
                  <Skeleton className="h-4 w-24" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-16" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : data ? (
          <>
            {/* Overview Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Compliance Score</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{data.complianceScore}%</div>
                  <p className="text-xs text-muted-foreground">Overall compliance</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Drift Detections</CardTitle>
                  <Eye className="h-4 w-4 text-yellow-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{data.driftDetections}</div>
                  <p className="text-xs text-muted-foreground">Configuration drifts</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Policy Violations</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{data.policyViolations}</div>
                  <p className="text-xs text-muted-foreground">Active violations</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Last Scan</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {data.lastScan ? formatTimeAgo(data.lastScan) : 'Never'}
                  </div>
                  <p className="text-xs text-muted-foreground">Security scan</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {/* Compliance Frameworks */}
              <Card>
                <CardHeader>
                  <CardTitle>Compliance Frameworks</CardTitle>
                  <CardDescription>Status across regulatory frameworks</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {data.frameworks.map((framework) => (
                      <div
                        key={framework.name}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-muted-foreground" />
                          <div className="font-medium">{framework.name}</div>
                          {getStatusBadge(framework.status)}
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">{framework.score}%</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Events */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Events</CardTitle>
                  <CardDescription>Latest governance and security events</CardDescription>
                </CardHeader>
                <CardContent>
                  {data.recentEvents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                      <CheckCircle2 className="h-12 w-12 mb-2 text-green-500" />
                      <p>No recent events</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {data.recentEvents.map((event) => (
                        <div
                          key={event.id}
                          className="flex items-start gap-3 p-3 rounded-lg border"
                        >
                          {getSeverityIcon(event.severity)}
                          <div className="flex-1">
                            <p className="text-sm font-medium">{event.description}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatTimeAgo(event.timestamp)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
